import type { Predicate, PredicateValue } from "@procore/bim-index";

import type { PropertyQuery, PropertyTerm } from "dataAccess";

/**
 * `PropertyQuery` translated into the engine's predicate DSL.
 *
 * Kept apart from `bimIndexOfflineDataAccess` because it is the one piece of that
 * adapter with no I/O in it: a pure mapping between two query algebras, and the part
 * most worth testing without an index to run against. Being I/O-free is also what
 * makes it shared rather than duplicated — the predicate an index answers is the same
 * whether the files are local or a server is asked.
 */

/** What the interface's well-known property names are called inside the index. */
const SYNTHETIC_PROPERTIES: Record<string, string> = { id: "$id", path: "$path", name: "$name" };

/**
 * Most values the engine will plan for in one `IN` predicate ("IN predicate has N
 * values, exceeds limit of 500").
 */
export const MAX_IN_VALUES = 500;

/**
 * One term as a predicate.
 *
 * The index's own names for identity are synthetic (`$id`, `$path`, `$name`), so the
 * well-known names the interface accepts are translated here — that mapping is
 * exactly the kind of thing this adapter exists for. `negated` inverts any predicate,
 * but the engine answers it by complementing against `live_objects.rbm`, so a term
 * with `negate` needs that section present (see `protocol.ts`).
 */
function termPredicate(term: PropertyTerm): Predicate {
    const property = SYNTHETIC_PROPERTIES[term.property] ?? term.property;
    const negated = term.negate;

    if (term.defined) {
        return { operator: "DEFINED", property, negated };
    }
    if (term.range) {
        return { operator: "RANGE", property, min: term.range.min, max: term.range.max, negated };
    }

    // `$id` is a numeric column; every other value compares as the string it is.
    const values: PredicateValue[] =
        property === "$id" ? term.anyOf.map(Number).filter((value) => !Number.isNaN(value)) : [...term.anyOf];

    if (values.length <= MAX_IN_VALUES) {
        return { operator: "IN", property, values, negated };
    }

    // Callers legitimately ask about a whole selection — hundreds of thousands of ids
    // — so the value limit is handled here rather than made the caller's problem. An
    // `OR` of chunks keeps it to one query and one branch of the enclosing `AND`,
    // where a query per chunk could not be combined with the other terms at all. The
    // nesting stays two deep however many chunks there are, so it does not approach
    // the engine's predicate depth limit.
    const chunks: Predicate[] = [];
    for (let from = 0; from < values.length; from += MAX_IN_VALUES) {
        chunks.push({ operator: "IN", property, values: values.slice(from, from + MAX_IN_VALUES) });
    }
    return { operator: "OR", predicates: chunks, negated };
}

/**
 * The whole query as one predicate, or `undefined` when it asks for nothing.
 *
 * A single term is passed on unwrapped, so the common "these ids" query is the same
 * predicate it always was. An empty query answers `undefined` rather than an empty
 * `AND`, which the engine treats as matching everything — the opposite of what
 * `PropertyQuery` promises.
 */
export function queryPredicate({ terms }: PropertyQuery): Predicate | undefined {
    if (!terms.length) {
        return undefined;
    }
    const predicates = terms.map(termPredicate);
    return predicates.length === 1 ? predicates[0]! : { operator: "AND", predicates };
}
